import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHospitalDetailContext } from '../hospital-detail-context'

import { ResponsiveSizeV2 } from '@/components/atomic/_enums'
import Section from '@/components/atomic/molecules/Section'
import Tab from '@/features/hospital/components/tab'
import { useGlobalState } from '@/hooks/GlobalState'
import { useScroll } from '@/hooks/Scroll'
import { useTranslation } from '@/lang'
import theme from '@/theme'

import type { ExtendTabItem } from '../types'

const TAB_HEIGHT = 52

function getScrollTargetMargin(isHiddenTopDownloadBanner: boolean): number {
  const topDownloadBannerHeight =
    window.innerWidth > ResponsiveSizeV2.DESKTOP_MIN_WIDTH || isHiddenTopDownloadBanner
      ? 0
      : theme.variables.topDownloadBannerHeight

  return TAB_HEIGHT + theme.variables.globalHeaderHeight + document.documentElement.offsetTop + topDownloadBannerHeight
}

export const HospitalDetailTabNavigation = () => {
  const { t } = useTranslation()
  const { topDownloadBanner } = useGlobalState()
  const { hospital, events, reviews, analytics, getSectionRef } = useHospitalDetailContext()
  const { isInit, addTarget, initScroll, updateTargetMargin, subscribe, unsubscribe, scrollToTarget } = useScroll()
  const [tabIndex, setTabIndex] = useState(-1)

  const isEventVisible = events.length > 0
  const hasReview = hospital.statistic?.reviewCount > 0

  // Memoize click handlers to prevent infinite re-renders
  const handleReviewClick = useCallback(() => {
    scrollToTarget('review')
    analytics.trackTabClick('review')
  }, [scrollToTarget, analytics])

  const handleHospitalInfoClick = useCallback(() => {
    scrollToTarget('hospitalInfo')
    analytics.trackTabClick('hospitalInfo')
  }, [scrollToTarget, analytics])

  const handleEventClick = useCallback(() => {
    scrollToTarget('event')
    analytics.trackTabClick('event')
  }, [scrollToTarget, analytics])

  const tabItems: ExtendTabItem[] = useMemo(
    () => [
      {
        text: t('templates.hospitalDetail.tabs.review'),
        id: 'review',
        onClick: handleReviewClick,
        isVisible: hasReview,
      },
      {
        text: t('templates.hospitalDetail.tabs.hospital'),
        id: 'hospitalInfo',
        onClick: handleHospitalInfoClick,
        isVisible: true,
      },
      {
        text: t('templates.hospitalDetail.tabs.event'),
        id: 'event',
        onClick: handleEventClick,
        isVisible: isEventVisible,
      },
    ],
    [t, hasReview, isEventVisible, handleReviewClick, handleHospitalInfoClick, handleEventClick],
  )

  useEffect(() => {
    // Initialize scroll if not already initialized
    if (!isInit) {
      initScroll()
      return
    }
  }, [isInit, initScroll])

  useEffect(() => {
    if (!isInit) return

    // Get section refs from context
    const hospitalInfoElement = getSectionRef('hospitalInfo')
    const reviewElement = getSectionRef('review')
    const eventElement = getSectionRef('event')

    // Wait for refs to be ready
    if (!hospitalInfoElement || !reviewElement || !eventElement) {
      return
    }

    const margin = -getScrollTargetMargin(topDownloadBanner.isHidden)
    addTarget('hospitalInfo', {
      element: hospitalInfoElement,
      margin,
    })
    addTarget('review', { element: reviewElement, margin })
    addTarget('event', { element: eventElement, margin })
  }, [isInit, addTarget, topDownloadBanner.isHidden, getSectionRef])

  useEffect(() => {
    const onReach = (e: { targetId: string }) => {
      // Create visible tab items on the fly to avoid dependency issues
      const visibleTabItems = [
        { id: 'review', isVisible: hasReview },
        { id: 'hospitalInfo', isVisible: true },
        { id: 'event', isVisible: isEventVisible },
      ].filter((item) => item.isVisible)

      const tabIndex = visibleTabItems.findIndex((item) => item.id === e.targetId)
      setTabIndex(tabIndex)
    }

    subscribe('reach', onReach)

    return () => {
      unsubscribe('reach', onReach)
    }
  }, [subscribe, unsubscribe, hasReview, isEventVisible])

  useEffect(() => {
    const margin = -getScrollTargetMargin(topDownloadBanner.isHidden)
    updateTargetMargin('hospitalInfo', margin)
    updateTargetMargin('review', margin)
    updateTargetMargin('event', margin)
  }, [updateTargetMargin, topDownloadBanner.isHidden])

  // Only show tabs if there's more than one visible tab
  if (tabItems.filter((item) => item.isVisible).length <= 1) {
    return null
  }

  // Calculate dynamic top position based on banner visibility
  const getTopPosition = () => {
    const hasTopBanner = !topDownloadBanner.isHidden
    const topDownloadBannerHeight = theme.variables.topDownloadBannerHeight
    const globalHeaderHeight = theme.variables.globalHeaderHeight

    if (hasTopBanner) {
      return `${topDownloadBannerHeight + globalHeaderHeight}px`
    }
    return `${globalHeaderHeight}px`
  }

  return (
    <Section
      className={`tabSection visible sticky z-10`}
      style={{ top: getTopPosition() }}
      padding={0}
      hasDivider={false}
    >
      <Tab index={tabIndex} items={tabItems} />
    </Section>
  )
}
